import { useState, useEffect, useCallback, useRef } from "react";
import RouteValidationService from "../../competitiveAnalysisModal/components/RouteValidation/lib/service";
import { occupantMatchesFavoriteType } from "../multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";
import type { MissingRailFreightSectionItem } from "../../../types/IMissingRailFreight";
import {
  PublicTariffStatus,
  UsePublicTariffCheckParams,
} from "../../../types/IRailRateRequest";
import NitroService from "../../../utils/nitro.service";

export function usePublicTariffCheck({
  quoteParams,
  destination,
  selectedProduct,
  product,
  selectedMonth,
  originManager,
  getLocationDetailsWithRetry,
  onCompetitorFreightFound,
}: UsePublicTariffCheckParams) {
  const [publicTariffStatusByOriginKey, setPublicTariffStatusByOriginKey] =
    useState<Record<string, PublicTariffStatus>>({});
  const [publicTariffRateByOriginKey, setPublicTariffRateByOriginKey] =
    useState<Record<string, string>>({});
  const publicTariffStatusByOriginKeyRef = useRef<
    Record<string, PublicTariffStatus>
  >({});
  const activeCheckTokenByOriginKeyRef = useRef<Record<string, number>>({});
  const checkStartedAtByOriginKeyRef = useRef<Record<string, number>>({});
  const checkOriginByOriginKeyRef = useRef<
    Record<string, MissingRailFreightSectionItem>
  >({});
  const quotesByDestinationInFlightRef = useRef<Promise<any[]> | null>(null);
  const checkSequenceRef = useRef(0);
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    publicTariffStatusByOriginKeyRef.current = publicTariffStatusByOriginKey;
  }, [publicTariffStatusByOriginKey]);

  useEffect(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;
      activeCheckTokenByOriginKeyRef.current = {};
      checkStartedAtByOriginKeyRef.current = {};
      checkOriginByOriginKeyRef.current = {};
    };
  }, []);

  const clearPublicTariffStatus = useCallback((originKey: string) => {
    setPublicTariffStatusByOriginKey((prev) => {
      const next = { ...prev };
      delete next[originKey];
      return next;
    });
    setPublicTariffRateByOriginKey((prev) => {
      if (!(originKey in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[originKey];
      return next;
    });
  }, []);

  const getQuotesByDestinationShared = useCallback(async (): Promise<any[]> => {
    if (quotesByDestinationInFlightRef.current) {
      return quotesByDestinationInFlightRef.current;
    }

    const inFlightRequest = originManager
      ? NitroService.getQuotesByDestination(quoteParams, 0, 0)
      : NitroService.getQuotesByDestinationOld(quoteParams, 0, 0);
    inFlightRequest
      .then((response) => response || [])
      .finally(() => {
        quotesByDestinationInFlightRef.current = null;
      });

    quotesByDestinationInFlightRef.current = inFlightRequest;
    return inFlightRequest;
  }, [originManager, quoteParams]);

  const handleCheckPublicTariffForOrigin = useCallback(
    async (railOrigin: MissingRailFreightSectionItem) => {
      const pollIntervalMs = 5000;
      const pollDurationMs = 60000;
      const attemptTimeoutMs = 4500;
      const pollStartAt = Date.now();
      const pollDeadlineAt = pollStartAt + pollDurationMs;

      const setNotAvailable = () => {
        setPublicTariffStatusByOriginKey((prev) => ({
          ...prev,
          [railOrigin.originKey]: "rate-not-found",
        }));
        setPublicTariffRateByOriginKey((prev) => {
          if (!(railOrigin.originKey in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[railOrigin.originKey];
          return next;
        });
      };

      const setNotFoundWithRetry = () => {
        setPublicTariffStatusByOriginKey((prev) => ({
          ...prev,
          [railOrigin.originKey]: "rate-not-found-retry",
        }));
        setPublicTariffRateByOriginKey((prev) => {
          if (!(railOrigin.originKey in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[railOrigin.originKey];
          return next;
        });
      };

      const setNotAvailableWithRetry = () => {
        setPublicTariffStatusByOriginKey((prev) => ({
          ...prev,
          [railOrigin.originKey]: "rate-not-available-retry",
        }));
        setPublicTariffRateByOriginKey((prev) => {
          if (!(railOrigin.originKey in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[railOrigin.originKey];
          return next;
        });
      };

      const setExternalRate = (rateValue: number) => {
        setPublicTariffStatusByOriginKey((prev) => ({
          ...prev,
          [railOrigin.originKey]: "external-rate",
        }));
        setPublicTariffRateByOriginKey((prev) => ({
          ...prev,
          [railOrigin.originKey]: rateValue.toFixed(2),
        }));
      };

      const delay = (ms: number) =>
        new Promise<void>((resolve) => {
          setTimeout(resolve, ms);
        });

      const finishCheckTracking = () => {
        delete activeCheckTokenByOriginKeyRef.current[railOrigin.originKey];
        delete checkStartedAtByOriginKeyRef.current[railOrigin.originKey];
        delete checkOriginByOriginKeyRef.current[railOrigin.originKey];
      };

      const checkToken = checkSequenceRef.current + 1;
      checkSequenceRef.current = checkToken;
      activeCheckTokenByOriginKeyRef.current[railOrigin.originKey] = checkToken;
      checkStartedAtByOriginKeyRef.current[railOrigin.originKey] = pollStartAt;
      checkOriginByOriginKeyRef.current[railOrigin.originKey] = railOrigin;

      let railRateRequestFailedEncountered = false;

      setPublicTariffStatusByOriginKey((prev) => ({
        ...prev,
        [railOrigin.originKey]: "checking",
      }));
      setPublicTariffRateByOriginKey((prev) => {
        if (!(railOrigin.originKey in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[railOrigin.originKey];
        return next;
      });

      const isCurrentCheckActive = () =>
        !isUnmountedRef.current &&
        activeCheckTokenByOriginKeyRef.current[railOrigin.originKey] ===
          checkToken;

      const findRailQuote = (quotesByDestination: any[]): any => {
        const destinationMatch = quotesByDestination.find(
          (entry) => entry?.destination?.id === destination.id,
        );

        const findInEntry = (entry: any) =>
          entry?.quotes?.find(
            (quoteEntry: any) =>
              quoteEntry?.origin?.id === railOrigin.originId &&
              (quoteEntry?.mode || "").toUpperCase() === "RAIL",
          );

        const destinationQuoteMatch = findInEntry(destinationMatch);
        if (destinationQuoteMatch) {
          return destinationQuoteMatch;
        }

        return (
          quotesByDestination
            .map((entry) => findInEntry(entry))
            .find((quoteMatch) => !!quoteMatch) || null
        );
      };

      const evaluateOnce = async (): Promise<"resolved" | "continue"> => {
        const quotesByDestination = await getQuotesByDestinationShared();

        if (!isCurrentCheckActive()) {
          return "resolved";
        }

        const quote = findRailQuote(quotesByDestination || []);
        const totalFreight = Number((quote?.freight as any)?.totalFreight);
        const hasFreight = Number.isFinite(totalFreight);

        if (hasFreight) {
          onCompetitorFreightFound?.(railOrigin, quote);

          if (railOrigin.competitor) {
            clearPublicTariffStatus(railOrigin.originKey);
            finishCheckTracking();
            return "resolved";
          }

          setExternalRate(totalFreight);
          finishCheckTracking();
          return "resolved";
        }

        const locationDetails = await getLocationDetailsWithRetry(
          railOrigin.originId,
        ).catch(() => undefined);

        if (!isCurrentCheckActive()) {
          return "resolved";
        }

        const occupants = Array.isArray(locationDetails?.occupants)
          ? locationDetails.occupants
          : [];
        const matchedOccupant = occupants.find((occupant: any) =>
          occupantMatchesFavoriteType(occupant, railOrigin.quote),
        );

        if (!matchedOccupant?.id) {
          return "continue";
        }

        const validation = await RouteValidationService.validate({
          mode: "RAIL",
          product: selectedProduct || product || "AMMONIA",
          originLocationId: railOrigin.originId,
          originOccupantId: matchedOccupant.id,
          destinationId: destination.id,
          shipDate: selectedMonth || "",
          isCity: !!destination.isCity,
        });

        if (!isCurrentCheckActive()) {
          return "resolved";
        }

        if ((validation as any)?.railRateRequestFailed === true) {
          railRateRequestFailedEncountered = true;
          return "continue";
        }

        return "continue";
      };

      const evaluateOnceWithTimeout = async (): Promise<
        "resolved" | "continue"
      > => {
        const timeoutResult = new Promise<"continue">((resolve) => {
          setTimeout(() => resolve("continue"), attemptTimeoutMs);
        });

        return Promise.race<"resolved" | "continue">([
          evaluateOnce(),
          timeoutResult,
        ]);
      };

      try {
        while (isCurrentCheckActive() && Date.now() < pollDeadlineAt) {
          // eslint-disable-next-line no-await-in-loop
          const result = await evaluateOnceWithTimeout();
          if (result === "resolved") {
            return;
          }

          const remainingMs = pollDeadlineAt - Date.now();
          if (remainingMs <= 0) {
            break;
          }

          // eslint-disable-next-line no-await-in-loop
          await delay(Math.min(pollIntervalMs, remainingMs));
        }

        if (!isCurrentCheckActive()) {
          return;
        }

        // If railRateRequestFailed was encountered:
        // - For competitor: show "Tariff Rate Not Found" with "Check Again" button
        // - For non-competitor: show "Tariff Rate Not Found" without retry button
        // If railRateRequestFailed was NOT encountered:
        // - Show "Tariff Rate Not Available" with "Check Again" button
        if (railRateRequestFailedEncountered) {
          if (railOrigin.competitor) {
            setNotFoundWithRetry();
          } else {
            setNotAvailable();
          }
        } else {
          setNotAvailableWithRetry();
        }
        finishCheckTracking();
      } catch (error) {
        console.error("public tariff check failed", error);
        if (!isCurrentCheckActive()) {
          finishCheckTracking();
          return;
        }
        if (railRateRequestFailedEncountered) {
          if (railOrigin.competitor) {
            setNotFoundWithRetry();
          } else {
            setNotAvailable();
          }
        } else {
          setNotAvailableWithRetry();
        }
        finishCheckTracking();
      }
    },
    [
      clearPublicTariffStatus,
      destination.id,
      destination.isCity,
      getLocationDetailsWithRetry,
      getQuotesByDestinationShared,
      onCompetitorFreightFound,
      product,
      selectedMonth,
      selectedProduct,
    ],
  );

  return {
    publicTariffStatusByOriginKey,
    publicTariffRateByOriginKey,
    handleCheckPublicTariffForOrigin,
  };
}
